import React, { useState } from 'react';
import { Download, FileText, Database, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { exportData, downloadExportedFile } from '../../services/api';

const ExportPanel = ({ onExport, status, apiBaseUrl }) => {
  const [exportFormat, setExportFormat] = useState('excel');
  const [dataType, setDataType] = useState('analyzed');
  const [includeRawData, setIncludeRawData] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportHistory, setExportHistory] = useState([]);

  const exportFormats = [
    {
      id: 'excel',
      name: 'Excel (.xlsx)',
      description: 'Spreadsheet format with multiple sheets',
      icon: '📊',
      color: 'green'
    },
    {
      id: 'pdf',
      name: 'PDF Report',
      description: 'Formatted report with charts and analysis',
      icon: '📄',
      color: 'red'
    }
  ];

  const dataTypes = [
    {
      id: 'raw',
      name: 'Raw Data',
      description: 'Original articles and content',
      icon: '📰',
      color: 'blue'
    },
    {
      id: 'analyzed',
      name: 'Analyzed Data',
      description: 'Processed with NLP analysis',
      icon: '🧠',
      color: 'purple'
    },
    {
      id: 'both',
      name: 'Both',
      description: 'Raw and analyzed data combined',
      icon: '📋',
      color: 'orange'
    }
  ];

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Call the export API
      const result = await exportData(exportFormat, dataType, includeRawData);
      
      // Add to export history
      const newExport = {
        id: Date.now(),
        format: exportFormat,
        dataType: dataType,
        timestamp: new Date().toLocaleString(),
        status: 'success',
        filePath: result.file_path,
        fileSize: result.file_size,
        recordsExported: result.records_exported
      };
      setExportHistory(prev => [newExport, ...prev.slice(0, 4)]); // Keep last 5
      
      // Call the parent onExport callback if provided
      if (onExport) {
        await onExport(exportFormat, result);
      }
      
      // Show success message
      alert(`Export successful! ${result.records_exported} records exported to ${result.file_path}`);
      
    } catch (error) {
      const newExport = {
        id: Date.now(),
        format: exportFormat,
        dataType: dataType,
        timestamp: new Date().toLocaleString(),
        status: 'error',
        error: error.message
      };
      setExportHistory(prev => [newExport, ...prev.slice(0, 4)]);
      
      // Show error message
      alert(`Export failed: ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownload = async (filePath) => {
    try {
      await downloadExportedFile(filePath);
    } catch (error) {
      alert(`Download failed: ${error.message}`);
    }
  };

  const getFormatColor = (formatId) => {
    const format = exportFormats.find(f => f.id === formatId);
    return format?.color || 'gray';
  };

  const getDataTypeColor = (typeId) => {
    const type = dataTypes.find(t => t.id === typeId);
    return type?.color || 'gray';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* Data Type Selection */}
      <div>
        <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">
          Data Type to Export
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {dataTypes.map((type) => (
            <label
              key={type.id}
              className={`relative cursor-pointer p-3 rounded-lg border-2 transition-all ${
                dataType === type.id
                  ? `border-${type.color}-500 bg-${type.color}-50 dark:bg-${type.color}-900/20`
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <input
                type="radio"
                name="dataType"
                value={type.id}
                checked={dataType === type.id}
                onChange={(e) => setDataType(e.target.value)}
                className="sr-only"
              />
              <div className="flex items-center space-x-3">
                <div className="text-2xl">{type.icon}</div>
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {type.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {type.description}
                  </div>
                </div>
              </div>
              {dataType === type.id && (
                <div className={`absolute top-2 right-2 w-3 h-3 bg-${type.color}-500 rounded-full`}></div>
              )}
            </label>
          ))}
        </div>
      </div>

      {/* Export Format Selection */}
      <div>
        <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">
          Export Format
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {exportFormats.map((format) => (
            <label
              key={format.id}
              className={`relative cursor-pointer p-3 rounded-lg border-2 transition-all ${
                exportFormat === format.id
                  ? `border-${format.color}-500 bg-${format.color}-50 dark:bg-${format.color}-900/20`
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <input
                type="radio"
                name="exportFormat"
                value={format.id}
                checked={exportFormat === format.id}
                onChange={(e) => setExportFormat(e.target.value)}
                className="sr-only"
              />
              <div className="flex items-center space-x-3">
                <div className="text-2xl">{format.icon}</div>
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {format.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {format.description}
                  </div>
                </div>
              </div>
              {exportFormat === format.id && (
                <div className={`absolute top-2 right-2 w-3 h-3 bg-${format.color}-500 rounded-full`}></div>
              )}
            </label>
          ))}
        </div>
      </div>

      {/* Export Options */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
        <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-3">
          Export Options
        </h4>
        <div className="space-y-3">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={includeRawData}
              onChange={(e) => setIncludeRawData(e.target.checked)}
              className="text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Include raw content (may increase file size)
            </span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              defaultChecked
              disabled
              className="text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Include metadata and analysis results
            </span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              defaultChecked
              disabled
              className="text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Include summary statistics
            </span>
          </label>
        </div>
      </div>

      {/* Export Button */}
      <div className="space-y-3">
        <button
          onClick={handleExport}
          disabled={isExporting}
          className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
            isExporting
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
          }`}
        >
          {isExporting ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Exporting...</span>
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-2">
              <Download className="w-5 h-5" />
              <span>Export Data</span>
            </div>
          )}
        </button>

        <div className="text-sm text-gray-600 dark:text-gray-400 text-center">
          Files will be saved to the exports directory
        </div>
      </div>

      {/* Export History */}
      {exportHistory.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-3">
            Recent Exports
          </h4>
          <div className="space-y-2">
            {exportHistory.map((exportItem) => (
              <div
                key={exportItem.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  exportItem.status === 'success'
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`text-lg`}>
                    {exportFormats.find(f => f.id === exportItem.format)?.icon}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white text-sm">
                      {exportFormats.find(f => f.id === exportItem.format)?.name} - {dataTypes.find(t => t.id === exportItem.dataType)?.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {exportItem.timestamp}
                    </div>
                    {exportItem.status === 'success' && exportItem.recordsExported && (
                      <div className="text-xs text-green-600 dark:text-green-400">
                        {exportItem.recordsExported} records exported
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {exportItem.status === 'success' && exportItem.filePath && (
                    <button
                      onClick={() => handleDownload(exportItem.filePath)}
                      className="p-1 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded"
                      title="Download file"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  )}
                  <div className={`text-xs px-2 py-1 rounded-full ${
                    exportItem.status === 'success'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-400'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-400'
                  }`}>
                    {exportItem.status === 'success' ? 'Success' : 'Failed'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Export Information */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
        <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
          Export Information
        </h4>
        <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <li>• <strong>Raw Data:</strong> Original articles, headlines, and content</li>
          <li>• <strong>Analyzed Data:</strong> NLP processed with PESTLE analysis, entities, and insights</li>
          <li>• <strong>Excel:</strong> Multiple sheets with raw and analyzed data</li>
          <li>• <strong>PDF:</strong> Formatted report with charts and insights</li>
        </ul>
      </div>

      {/* API Endpoint Info */}
      <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
        <div className="text-xs text-gray-600 dark:text-gray-400">
          <strong>API Endpoint:</strong> {apiBaseUrl}/export
        </div>
      </div>
    </div>
  );
};

export default ExportPanel; 